import { useRef } from "react";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faPlay } from "@fortawesome/free-solid-svg-icons";
import YouTubeFacade from "../ui/YouTubeFacade";

// On Air: one stage (the selected image or trailer, 16:9 on a stage-navy
// monitor) and a strip of hard-edged thumbnails, the selected one ringed in
// signal blue. The strip runs under the stage on phones and as a column beside
// it from md up. Videos play on the stage through the cookieless facade.
const GameImageGallery = ({ imageData, currentIndex, setCurrentIndex }) => {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const total = imageData?.length ?? 0;
  const current = imageData?.[currentIndex] ?? imageData?.[0];

  // Keep the selected thumbnail in view inside the strip only (never scrolls the page).
  const revealThumb = (index: number) => {
    const strip = stripRef.current;
    const thumb = strip?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    if (!strip || !thumb) return;
    const vertical = strip.scrollHeight > strip.clientHeight + 1 && strip.scrollWidth <= strip.clientWidth + 1;
    strip.scrollTo(
      vertical
        ? { top: thumb.offsetTop - (strip.clientHeight - thumb.offsetHeight) / 2, behavior: "smooth" }
        : { left: thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: "smooth" }
    );
  };

  const select = (index: number) => {
    setCurrentIndex(index);
    revealThumb(index);
  };
  const step = (delta: number) => select((currentIndex + delta + total) % total);

  if (!current) return null;

  return (
    <div className="flex w-full flex-col gap-3 md:flex-row">
      {/* Stage */}
      <div className={`relative w-full ${total > 1 ? "md:w-4/5" : ""}`}>
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-[3px] bg-stage-deep">
          {current.type === "video" ? (
            <YouTubeFacade
              key={currentIndex}
              url={current.videoUrl}
              title="Game video"
              className="absolute left-0 top-0 h-full w-full"
            />
          ) : (
            <Image
              src={current.imageUrl}
              alt={`Gallery Image ${currentIndex + 1}`}
              fill
              sizes="(max-width: 768px) 100vw, 80vw"
              className="object-contain"
            />
          )}
          {total > 1 ? (
            <span className="pointer-events-none absolute left-0 top-0 bg-stage-deep px-2.5 py-1 font-display text-sm font-semibold tabular-nums text-white">
              {currentIndex + 1} / {total}
            </span>
          ) : null}
        </div>
        {total > 1 ? (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous image"
              className="absolute left-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center bg-stage-deep text-white transition-colors duration-150 hover:bg-signal"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next image"
              className="absolute right-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center bg-stage-deep text-white transition-colors duration-150 hover:bg-signal"
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
            </button>
          </>
        ) : null}
      </div>

      {/* Strip: a row under the stage on phones, a column as tall as the stage from md up */}
      {total > 1 ? (
        <div className="relative md:w-1/5">
          <div
            ref={stripRef}
            role="group"
            aria-label="Gallery thumbnails"
            className="scrollbar-hide relative flex gap-2 overflow-x-auto p-0.5 md:absolute md:inset-0 md:flex-col md:gap-3 md:overflow-y-auto md:overflow-x-hidden"
          >
            {imageData.map((img, index) => {
              const selected = currentIndex === index;
              return (
                <button
                  key={index}
                  type="button"
                  data-index={index}
                  onClick={() => select(index)}
                  aria-label={`${img.type === "video" ? "Video" : "Image"} ${index + 1} of ${total}`}
                  aria-pressed={selected}
                  className={`relative aspect-[16/9] w-[104px] shrink-0 overflow-hidden rounded-[3px] bg-theme-surface transition-opacity duration-150 md:w-full ${
                    selected ? "opacity-100 ring-2 ring-signal" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <Image
                    src={img.imageUrl}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    sizes="(max-width: 768px) 104px, 20vw"
                    className="object-cover"
                  />
                  {img.type === "video" && (
                    <span className="absolute bottom-0 left-0 flex h-6 w-7 items-center justify-center bg-stage-deep text-white">
                      <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GameImageGallery;
